import React, { useEffect, useMemo, useRef, useState } from 'react'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import NormalText from 'common/Typography/NormalText'

const columns = [
  {
    field: 'userId',
    headerName: 'User ID',
    maxWidth: 400,
    renderCell: (res) => {
      return (
        <Stack>
          <Tooltip
            placement='top'
            title={
              <Typography>
                {res.row?.userId.map((id) => {
                  return (
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: 'white'
                      }}
                      key={id}
                    >
                      {id}
                    </Typography>
                  )
                })}
              </Typography>
            }
          >
            <Typography>
              {res.row?.userId.map((id) => {
                return (
                  <Typography
                    sx={{
                      fontSize: '14px'
                    }}
                    key={id}
                  >
                    {id}
                  </Typography>
                )
              })}
            </Typography>
          </Tooltip>
        </Stack>
      )
    }
  },
  {
    field: 'status',
    headerName: 'Sent status',
    maxWidth: 100,
    width: 50,
    renderCell: (res) => {
      if (res.row?.status === 'error') {
        return <Typography color='red'>Error</Typography>
      } else {
        return <Typography color='green'>Success</Typography>
      }
    }
  },
  {
    field: 'message',
    headerName: 'Message',
    renderCell: (res) => {
      if (res.row?.status === 'error') {
        return (
          <Typography sx={{ fontSize: '14px' }}>{res.row?.message}</Typography>
        )
      } else {
        return <Typography>nil</Typography>
      }
    }
  },
  {
    field: 'details.error',
    headerName: 'Error',
    width: 50,
    renderCell: (res) => {
      if (res.row?.status === 'error') {
        return (
          <Typography sx={{ fontSize: '14px' }}>
            {res.row?.details?.error}
          </Typography>
        )
      } else {
        return <Typography>nil</Typography>
      }
    }
  },
  {
    field: 'pushToken',
    headerName: 'pushToken',
    width: 50
  }
]

const checkCSV = (csvFile: string) => {
  const csvFileRows: string[] = csvFile.split('\n')
  const formattedRows = csvFileRows.map((row) =>
    row.replaceAll('"', '').trim().split(',')
  )
  const formattedCSVRows = formattedRows.flat()
  if (formattedCSVRows[0] !== 'user_id') {
    return {
      status: 'ERROR',
      message: 'Invalid csv format, please check your column headers',
      numberOfUsers: 0
    }
  }
  if (formattedCSVRows.includes('')) {
    return {
      status: 'ERROR',
      message: 'Invalid data, please check as at least one row is empty',
      numberOfUsers: 0
    }
  } else
    return {
      status: 'OK',
      message: '',
      numberOfUsers:
        formattedCSVRows.length >= 2 ? formattedCSVRows.length - 1 : 0
    }
}

import { toast } from 'react-toastify'
import { useMutation } from '@tanstack/react-query'
import NotificationAPI from 'api/faber/NotificationAPI'
import { RoundedButton } from 'common/Button/Button'
import CrossDialogBtn from 'common/Button/CrossDialogBtn'
import RoundedFilledLoadingButton from 'common/Button/RoundedFilledLoadingButton'
import OrbitSession from 'hooks/useCookies'

import NotificationScreenshot from '../../../assets/notification_screenshot.png'

type PushNotificationRes = {
  status: 'ok' | 'error'
  message: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  details: any
  id: string
  userId: string | string[]
}
function PushNotificationForm() {
  const [formState, setFormState] = useState<'INITIAL' | 'FORM_SUBMITTED'>(
    'INITIAL'
  )
  const [isDialogOpen, setIsDialogOpen] = React.useState(false)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [uploadedObject, setUploadedObject] = useState<any>(null)
  const [uploadedFilename, setUploadedFilename] = useState<string>('')
  const [messageBody, setMessageBody] = useState<string>('')
  const [messageTitle, setMessageTitle] = useState<string>('')
  const [uploadErrMessage, setUploadErrMessage] = useState<string>('')
  const [numberOfUsers, setNumberOfUsers] = useState<number>(0)
  const faberToken = OrbitSession.get('faberToken')
  const [sendPushErr, setSendPushErr] = useState(false)
  const [submittedUsersResults, setSubmittedUsersResults] = useState<
    PushNotificationRes[]
  >([])

  const fileUploadBtnRef = useRef<HTMLInputElement>(null)

  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false)

  const resetAll = () => {
    setMessageBody('')
    setUploadErrMessage('')
    setSendPushErr(false)
    setUploadedFilename('')
    setUploadedObject(null)
    if (fileUploadBtnRef.current) {
      fileUploadBtnRef.current.value = ''
    }
  }
  useEffect(() => {
    setFormState('INITIAL')
    resetAll()
  }, [])

  const formattedPushNotificationRes = useMemo(() => {
    const numberOfSuccessfulCases = submittedUsersResults.filter(
      (res) => res.status === 'ok'
    ).length

    return {
      numberOfSuccessfulCases,
      numberOfFailedCases:
        submittedUsersResults.length - numberOfSuccessfulCases,
      numberOfTotalCases: submittedUsersResults.length,
      failedCases: submittedUsersResults.filter(
        (res) => res.status === 'error'
      ),
      allCases: submittedUsersResults
    }
  }, [submittedUsersResults])

  const {
    mutate: uploadFileAndSendPushNotification,
    isLoading: isUploadedFileAndSendPushNotificationLoading
  } = useMutation(NotificationAPI.uploadFileAndSendPushNotification, {
    onSuccess: (res) => {
      toast.success('Successfully uploaded CSV and sent push notification')
      setFormState('FORM_SUBMITTED')
      setSubmittedUsersResults(res.data)
      resetAll()
    },
    onError: () => {
      toast.error('There is a problem trying to upload your file to the server')
      setSendPushErr(true)
      resetAll()
    },
    onSettled: () => {
      setIsConfirmationDialogOpen(false)
    }
  })

  const sendNotification = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    file: any,
    messageBody: string,
    faberToken: string
  ) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('messageBody', messageBody)
    formData.append('messageTitle', messageTitle)
    uploadFileAndSendPushNotification({
      uploadObject: formData,
      faberToken: faberToken
    })
  }

  const onFileUpload = async (file) => {
    const fileName = file.name
    const reader = new FileReader()

    reader.onload = async (e) => {
      const text = e.target?.result

      if (typeof text !== 'string') {
        setUploadErrMessage('Please check your file and try again')

        return
      } else {
        const output = checkCSV(text)
        if (output.status === 'ERROR') {
          setUploadErrMessage(output.message)

          return null
        } else {
          setUploadErrMessage('')
          setNumberOfUsers(output.numberOfUsers)
          setUploadedObject(file)
        }
      }
    }

    reader.readAsText(file)
    setUploadedFilename(fileName)
    return
  }

  return (
    <>
      <Button
        variant='contained'
        onClick={() => {
          setIsDialogOpen(!isDialogOpen)
        }}
      >
        Fill form
      </Button>
      <Dialog
        open={isConfirmationDialogOpen}
        onClose={() => {
          setIsConfirmationDialogOpen(false)
        }}
      >
        <DialogContent>
          <Typography sx={{ fontWeight: 'bold' }}>
            Are you sure you want to send notifications to {numberOfUsers}{' '}
            users?
          </Typography>
          <Typography>
            This action is irreversible. Please double check your message body
            and once &quot;Confirm Send&quot; is clicked, the push notification
            will be sent to the selected users.
          </Typography>
          <Stack direction='row' justifyContent='center'>
            <RoundedFilledLoadingButton
              loading={isUploadedFileAndSendPushNotificationLoading}
              onClick={() =>
                sendNotification(uploadedObject, messageBody, faberToken)
              }
              variant='contained'
              sx={{
                width: '300px'
              }}
            >
              Confirm Send
            </RoundedFilledLoadingButton>
            <RoundedButton
              onClick={() => {
                setIsConfirmationDialogOpen(false)
              }}
            >
              Cancel
            </RoundedButton>
          </Stack>
        </DialogContent>
      </Dialog>
      <Dialog
        onClose={() => {
          setIsDialogOpen(false)
        }}
        fullScreen
        open={isDialogOpen}
        sx={{ width: '100%' }}
      >
        <Stack
          sx={{
            maxWidth: '1200px',
            mx: 'auto',
            width: '100%',
            height: '100%'
          }}
        >
          <Stack alignItems='flex-end' sx={{ position: 'absolute', right: 0 }}>
            <Stack sx={{ m: 2 }}>
              <CrossDialogBtn
                dialogClose={() => {
                  setIsDialogOpen(false)
                }}
              />
            </Stack>
          </Stack>
          <Stack
            sx={{
              p: 2,
              width: '100%',
              height: '100%',
              mt: '48px'
            }}
          >
            <Typography variant='h4'>
              Bulk send push notification to users
            </Typography>

            {formState === 'INITIAL' ? (
              <Stack sx={{ justifyContent: 'space-between', height: '100%' }}>
                <Stack>
                  <NormalText theme='muted'>
                    Example of how the push notification will look like:
                  </NormalText>
                  <img
                    src={NotificationScreenshot}
                    alt='notification-screenshot'
                    width={400}
                    height={180}
                  />
                  <Stack my={2}>
                    <Typography variant='h6'>
                      Message Title: (e.g. Jetpac Global)
                    </Typography>
                    <Typography
                      sx={{
                        color: 'red'
                      }}
                    >
                      ⚠️ Please do not include any PII. e.g. name, email, phone
                      number, etc. ⚠️
                    </Typography>
                    <TextField
                      sx={{ my: 1 }}
                      multiline
                      value={messageTitle}
                      onChange={(e) => {
                        setMessageTitle(e.target.value)
                      }}
                    />
                  </Stack>
                  <Stack my={2}>
                    <Typography variant='h6'>
                      Message body (below the Jetpac Global title):
                    </Typography>
                    <Typography
                      sx={{
                        color: 'red'
                      }}
                    >
                      ⚠️ Please do not include any PII. e.g. name, email, phone
                      number, etc. ⚠️
                    </Typography>
                    <TextField
                      sx={{ my: 1 }}
                      multiline
                      value={messageBody}
                      onChange={(e) => {
                        setMessageBody(e.target.value)
                      }}
                    />
                  </Stack>

                  <Stack>
                    <Typography variant='h6'>Upload file:</Typography>
                    <NormalText theme='muted'>
                      Note: <br />
                      - The file should be in .csv format <br />- Please upload
                      file containing user ids. The column should be named as
                      &quot;user_id&quot;
                    </NormalText>
                    <LoadingButton
                      variant='outlined'
                      component='label'
                      sx={{
                        fontSize: '12px',
                        p: 3,
                        background: 'white',
                        maxWidth: '300px'
                      }}
                      startIcon={<FileUploadIcon />}
                      loadingIndicator={<CircularProgress />}
                    >
                      Upload file
                      <input
                        ref={fileUploadBtnRef}
                        hidden
                        type='file'
                        accept='.csv'
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          if (!e.target.files) {
                            return
                          }
                          onFileUpload(e.target.files[0])
                        }}
                      />
                    </LoadingButton>
                    <Stack>
                      {uploadedFilename && (
                        <Stack direction='row' alignItems='flex-end'>
                          <NormalText>
                            Uploaded file:
                            <br /> <strong>{uploadedFilename}</strong>
                          </NormalText>
                          <Typography sx={{ color: 'red' }}>
                            (Uploaded for {numberOfUsers}{' '}
                            {numberOfUsers === 0 || numberOfUsers === 1
                              ? 'user'
                              : 'users'}
                            )
                          </Typography>
                        </Stack>
                      )}
                      {uploadErrMessage !== '' && (
                        <NormalText variant='subtitle1' sx={{ color: 'red' }}>
                          {uploadErrMessage}
                        </NormalText>
                      )}
                    </Stack>
                  </Stack>
                </Stack>
                <Stack
                  sx={{ alignItems: 'center', my: 4, justifyContent: 'center' }}
                  direction='row'
                >
                  {sendPushErr ? (
                    <Typography sx={{ color: 'red' }}>
                      Err in sending push notifications
                    </Typography>
                  ) : null}
                  <RoundedFilledLoadingButton
                    disabled={
                      !uploadedFilename ||
                      !messageBody ||
                      !!uploadErrMessage ||
                      !messageTitle
                    }
                    onClick={() => {
                      setIsConfirmationDialogOpen(!isConfirmationDialogOpen)
                    }}
                    variant='contained'
                    sx={{
                      width: '350px'
                    }}
                  >
                    Send push notifications to {numberOfUsers}{' '}
                    {numberOfUsers === 0 || numberOfUsers === 1
                      ? 'user'
                      : 'users'}
                  </RoundedFilledLoadingButton>
                  <RoundedButton onClick={resetAll}>Clear all</RoundedButton>
                </Stack>
              </Stack>
            ) : (
              <Stack>
                <Typography>
                  Total push notifications sent:{' '}
                  {formattedPushNotificationRes.numberOfTotalCases}
                </Typography>
                <Typography>
                  Number of failed cases:
                  {formattedPushNotificationRes.numberOfFailedCases}
                </Typography>
                <Typography>Details</Typography>
                <MUIDataGrid
                  rowHeight={80}
                  columns={columns}
                  rows={formattedPushNotificationRes.allCases}
                  getRowId={(row: PushNotificationRes) =>
                    `row-all-cases-${row.id}`
                  }
                />
                <Stack
                  direction='row'
                  sx={{
                    my: 2,
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <RoundedButton
                    onClick={() => {
                      setFormState('INITIAL')
                    }}
                  >
                    Back to previous page{' '}
                  </RoundedButton>
                  <RoundedButton
                    onClick={() => {
                      setIsDialogOpen(false)
                    }}
                  >
                    Cancel
                  </RoundedButton>
                </Stack>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Dialog>
    </>
  )
}

export default PushNotificationForm
